import React, { useState, useCallback } from "react";
import {
    Box,
    Paper,
    Typography,
    Grid,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    CircularProgress,
    Alert,
    Divider,
} from "@mui/material";
import AssessmentIcon from "@mui/icons-material/Assessment";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import MoneyOffIcon from "@mui/icons-material/MoneyOff";
import SavingsIcon from "@mui/icons-material/Savings";
import useAuthHeader from "react-auth-kit/hooks/useAuthHeader";
import { useLang } from "../../context/LanguageContext";
import { REACT_APP_BACKEND_URL } from "../../Utils/Language/urlBase";
import { formatDate, formatDateTime } from "../../Utils/dateFormatter";

const today = () => new Date().toISOString().split("T")[0];

const StatCard = ({ icon, label, value, color, subtitle }) => (
    <Paper
        elevation={0}
        sx={{
            p: 3,
            borderRadius: 3,
            background: color,
            color: "white",
            display: "flex",
            flexDirection: "column",
            gap: 1,
            height: "100%",
        }}
    >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box sx={{ opacity: 0.85, fontSize: 36 }}>{icon}</Box>
            <Box>
                <Typography
                    variant="body2"
                    sx={{ opacity: 0.85, fontWeight: 600 }}
                >
                    {label}
                </Typography>
                <Typography variant="h4" fontWeight={800} lineHeight={1.1}>
                    $
                    {Number(value).toLocaleString("en-US", {
                        minimumFractionDigits: 2,
                    })}
                </Typography>
                {subtitle && (
                    <Typography variant="caption" sx={{ opacity: 0.75 }}>
                        {subtitle}
                    </Typography>
                )}
            </Box>
        </Box>
    </Paper>
);

const DailyReport = () => {
    const { t } = useLang();
    const authHeader = useAuthHeader();
    const [startDate, setStartDate] = useState(today());
    const [endDate, setEndDate] = useState(today());
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchReport = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const resp = await fetch(
                `${REACT_APP_BACKEND_URL}/invoices/daily-report?startDate=${startDate}&endDate=${endDate}`,
                {
                    headers: {
                        Authorization: authHeader,
                        "Content-Type": "application/json",
                    },
                },
            );
            const result = await resp.json();
            if (!result.success) throw new Error(result.message || "Failed");
            setReport(result.data);
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }, [startDate, endDate, authHeader]);

    // Auto-fetch on first render
    React.useEffect(() => {
        fetchReport();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const net = report ? Number(report.summary.netCashInDrawer) : 0;
    const netColor =
        net >= 0
            ? "linear-gradient(135deg, #00897b 0%, #004d40 100%)"
            : "linear-gradient(135deg, #e53935 0%, #b71c1c 100%)";

    return (
        <Box sx={{ width: "100%" }}>
            {/* Page Header */}
            <Paper
                elevation={0}
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 3,
                    p: 2.5,
                    borderRadius: 3,
                    background:
                        "linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)",
                    color: "white",
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <AssessmentIcon sx={{ fontSize: 32, opacity: 0.9 }} />
                    <Box>
                        <Typography variant="h5" fontWeight={700}>
                            {t("DAILY_REPORT")}
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.8 }}>
                            {startDate === endDate
                                ? startDate
                                : `${startDate} → ${endDate}`}
                        </Typography>
                    </Box>
                </Box>

                {/* Date filters */}
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        flexWrap: "wrap",
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 0.3,
                        }}
                    >
                        <Typography variant="caption" sx={{ opacity: 0.8 }}>
                            {t("DATE")} ({t("FROM") || "From"})
                        </Typography>
                        <input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            style={{
                                padding: "6px 10px",
                                borderRadius: 8,
                                border: "1.5px solid rgba(255,255,255,0.4)",
                                background: "rgba(255,255,255,0.15)",
                                color: "white",
                                colorScheme: "dark",
                                fontSize: 14,
                            }}
                        />
                    </Box>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 0.3,
                        }}
                    >
                        <Typography variant="caption" sx={{ opacity: 0.8 }}>
                            {t("DATE")} ({t("TO") || "To"})
                        </Typography>
                        <input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            style={{
                                padding: "6px 10px",
                                borderRadius: 8,
                                border: "1.5px solid rgba(255,255,255,0.4)",
                                background: "rgba(255,255,255,0.15)",
                                color: "white",
                                colorScheme: "dark",
                                fontSize: 14,
                            }}
                        />
                    </Box>
                    <Button
                        variant="contained"
                        onClick={fetchReport}
                        disabled={loading}
                        sx={{
                            mt: 1.5,
                            bgcolor: "rgba(255,255,255,0.2)",
                            color: "white",
                            border: "1.5px solid rgba(255,255,255,0.4)",
                            fontWeight: 700,
                            "&:hover": { bgcolor: "rgba(255,255,255,0.35)" },
                        }}
                    >
                        {loading ? (
                            <CircularProgress
                                size={18}
                                sx={{ color: "white" }}
                            />
                        ) : (
                            t("SEARCH")
                        )}
                    </Button>
                </Box>
            </Paper>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {loading && !report && (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
                    <CircularProgress size={48} />
                </Box>
            )}

            {report && (
                <>
                    {/* Summary Cards */}
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={12} sm={6} lg={3}>
                            <StatCard
                                icon={<TrendingUpIcon fontSize="inherit" />}
                                label={t("SALES_INCOME")}
                                value={report.summary.salesIncome}
                                subtitle={`${report.summary.invoiceCount} ${t("INVOICE")}`}
                                color="linear-gradient(135deg, #1976d2 0%, #0d47a1 100%)"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} lg={3}>
                            <StatCard
                                icon={
                                    <AccountBalanceWalletIcon fontSize="inherit" />
                                }
                                label={t("CASH_COLLECTED")}
                                value={report.summary.cashCollected}
                                subtitle={`${report.summary.paymentCount} ${t("PAYMENT")}`}
                                color="linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} lg={3}>
                            <StatCard
                                icon={<MoneyOffIcon fontSize="inherit" />}
                                label={t("TOTAL_EXPENSES")}
                                value={report.summary.totalExpenses}
                                subtitle={`${report.summary.expenseCount} ${t("EXPENSES")}`}
                                color="linear-gradient(135deg, #e65100 0%, #bf360c 100%)"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} lg={3}>
                            <StatCard
                                icon={<SavingsIcon fontSize="inherit" />}
                                label={t("NET_CASH_IN_DRAWER")}
                                value={report.summary.netCashInDrawer}
                                subtitle={
                                    net >= 0 ? "✅ Positive" : "⚠ Negative"
                                }
                                color={netColor}
                            />
                        </Grid>
                    </Grid>

                    {/* Sales Invoices Table */}
                    <Paper sx={{ mb: 3, borderRadius: 2, overflow: "hidden" }}>
                        <Box
                            sx={{
                                px: 2.5,
                                py: 2,
                                bgcolor: "grey.50",
                                borderBottom: "1px solid #e0e0e0",
                            }}
                        >
                            <Typography variant="h6" fontWeight={700}>
                                📋 {t("SALES")} ({report.invoices.length})
                            </Typography>
                        </Box>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: "grey.100" }}>
                                        <TableCell sx={{ fontWeight: 700 }}>
                                            ID
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>
                                            {t("CUSTOMER_NAME")}
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>
                                            {t("TYPE")}
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>
                                            {t("DATE")}
                                        </TableCell>
                                        <TableCell
                                            align="right"
                                            sx={{ fontWeight: 700 }}
                                        >
                                            {t("TOTAL_PRICE")}
                                        </TableCell>
                                        <TableCell
                                            align="right"
                                            sx={{ fontWeight: 700 }}
                                        >
                                            {t("PAYMENT")}
                                        </TableCell>
                                        <TableCell
                                            align="right"
                                            sx={{ fontWeight: 700 }}
                                        >
                                            {t("REMAINING_AMOUNT")}
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {report.invoices.length === 0 ? (
                                        <TableRow>
                                            <TableCell
                                                colSpan={7}
                                                align="center"
                                                sx={{
                                                    py: 3,
                                                    color: "text.secondary",
                                                }}
                                            >
                                                {t("NO_ITEMS_ADDED")}
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        report.invoices.map((inv) => (
                                            <TableRow key={inv.id} hover>
                                                <TableCell>#{inv.id}</TableCell>
                                                <TableCell>
                                                    {inv.OtherParty?.name ??
                                                        "—"}
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={
                                                            inv.type?.name ??
                                                            inv.typeId
                                                        }
                                                        size="small"
                                                        color="primary"
                                                        variant="outlined"
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    {formatDateTime(
                                                        inv.createdAt,
                                                    )}
                                                </TableCell>
                                                <TableCell align="right">
                                                    $
                                                    {Number(
                                                        inv.priceFinal,
                                                    ).toFixed(2)}
                                                </TableCell>
                                                <TableCell
                                                    align="right"
                                                    sx={{
                                                        color: "success.main",
                                                        fontWeight: 600,
                                                    }}
                                                >
                                                    $
                                                    {Number(
                                                        inv.paidAmount,
                                                    ).toFixed(2)}
                                                </TableCell>
                                                <TableCell
                                                    align="right"
                                                    sx={{
                                                        color:
                                                            inv.remainingAmount >
                                                            0
                                                                ? "error.main"
                                                                : "text.secondary",
                                                        fontWeight: 600,
                                                    }}
                                                >
                                                    $
                                                    {Number(
                                                        inv.remainingAmount,
                                                    ).toFixed(2)}
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>

                    {/* Payments Table */}
                    <Paper sx={{ mb: 3, borderRadius: 2, overflow: "hidden" }}>
                        <Box
                            sx={{
                                px: 2.5,
                                py: 2,
                                bgcolor: "grey.50",
                                borderBottom: "1px solid #e0e0e0",
                            }}
                        >
                            <Typography variant="h6" fontWeight={700}>
                                💳 {t("PAYMENT_LIST")} ({report.payments.length}
                                )
                            </Typography>
                        </Box>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: "grey.100" }}>
                                        <TableCell sx={{ fontWeight: 700 }}>
                                            ID
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>
                                            {t("CUSTOMER_NAME")}
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>
                                            {t("AMOUNT")}
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>
                                            {t("CURRENCY")}
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>
                                            {t("EXCHANGE_RATE")}
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>
                                            {t("EMPLOYEE")}
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>
                                            {t("DATE")}
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {report.payments.length === 0 ? (
                                        <TableRow>
                                            <TableCell
                                                colSpan={7}
                                                align="center"
                                                sx={{
                                                    py: 3,
                                                    color: "text.secondary",
                                                }}
                                            >
                                                {t("PAYMENT_LIST_EMPTY")}
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        report.payments.map((p) => (
                                            <TableRow key={p.id} hover>
                                                <TableCell>#{p.id}</TableCell>
                                                <TableCell>
                                                    {p.invoice?.OtherParty
                                                        ?.name ?? "—"}
                                                </TableCell>
                                                <TableCell
                                                    sx={{
                                                        fontWeight: 600,
                                                        color: "success.main",
                                                    }}
                                                >
                                                    {Number(
                                                        p.amount,
                                                    ).toLocaleString()}
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={p.currency}
                                                        size="small"
                                                        color={
                                                            p.currency === "USD"
                                                                ? "success"
                                                                : "warning"
                                                        }
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    {Number(
                                                        p.rateAtPayment,
                                                    ).toLocaleString()}
                                                </TableCell>
                                                <TableCell>
                                                    {p.employee?.name ?? "—"}
                                                </TableCell>
                                                <TableCell>
                                                    {formatDateTime(
                                                        p.createdAt,
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>

                    {/* Expenses Table */}
                    <Paper sx={{ mb: 3, borderRadius: 2, overflow: "hidden" }}>
                        <Box
                            sx={{
                                px: 2.5,
                                py: 2,
                                bgcolor: "grey.50",
                                borderBottom: "1px solid #e0e0e0",
                            }}
                        >
                            <Typography variant="h6" fontWeight={700}>
                                💸 {t("EXPENSES")} ({report.expenses.length})
                            </Typography>
                        </Box>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: "grey.100" }}>
                                        <TableCell sx={{ fontWeight: 700 }}>
                                            {t("NAME")}
                                        </TableCell>
                                        <TableCell
                                            align="right"
                                            sx={{ fontWeight: 700 }}
                                        >
                                            {t("AMOUNT")} ($)
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>
                                            {t("EMPLOYEE")}
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>
                                            {t("DATE")}
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {report.expenses.length === 0 ? (
                                        <TableRow>
                                            <TableCell
                                                colSpan={4}
                                                align="center"
                                                sx={{
                                                    py: 3,
                                                    color: "text.secondary",
                                                }}
                                            >
                                                {t("NO_ITEMS_ADDED")}
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        report.expenses.map((e) => (
                                            <TableRow key={e.id} hover>
                                                <TableCell
                                                    sx={{ fontWeight: 600 }}
                                                >
                                                    {e.name}
                                                </TableCell>
                                                <TableCell
                                                    align="right"
                                                    sx={{
                                                        color: "error.main",
                                                        fontWeight: 600,
                                                    }}
                                                >
                                                    $
                                                    {Number(e.amount).toFixed(
                                                        2,
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {e.AddedBy?.name ?? "—"}
                                                </TableCell>
                                                <TableCell>
                                                    {formatDate(e.addedTime)}
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </>
            )}
        </Box>
    );
};

export default DailyReport;
